import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import {
  EquipmentApi,
  GatewayApi,
  GetResponse,
  Installation,
  Invitation,
  InvitationAccessLevel,
  InvitationsApi,
  InvitationStatusType,
  ViError,
} from '@vi-iot/client';
import { ViToastService } from '@vi-iot/ui';

import { AuthStore } from '../auth';
import { InvitationsStore } from '../invitations';

import { GatewayCommissioningActions } from './gateway-commissioning.actions';
import { GatewayCommissioningStore } from './gateway-commissioning.store';

@Injectable()
export class GatewayCommissioningEffects {
  constructor(
    private actions$: Actions,
    private toast: ViToastService,
    private translate: TranslateService,
    private equipmentApi: EquipmentApi,
    private invitationsApi: InvitationsApi,
    private invitationsStore: InvitationsStore,
    private authStore: AuthStore,
    private gatewayApi: GatewayApi,
    private gatewayCommissioningStore: GatewayCommissioningStore
  ) {}

  registerGateway$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.registerGateway.attempt),
      mergeMap((action) =>
        combineLatest([of(action), this.gatewayCommissioningStore.formData$.pipe(take(1))])
      ),
      switchMap(([action, formData]) =>
        this.equipmentApi
          .createInstallation({
            address: formData.address,
            description: formData.description,
            gateway: formData.gateway,
            heatingType: formData.heatingType,
            installationOwnerData: formData.installationOwnerData,
            withoutOwner: formData.isGrandmaFunctionSelected,
          })
          .pipe(
            map((response) => {
              const installationId = (response as GetResponse<Installation>).data?.id;
              const inviteeEmail = formData.email;

              if (action.payload.withoutOwner) {
                return GatewayCommissioningActions.registerGateway.success({
                  withoutOwner: action.payload.withoutOwner,
                });
              } else {
                return GatewayCommissioningActions.sendInvitation.attempt({
                  installationId,
                  inviteeEmail,
                });
              }
            }),
            catchError((error) => {
              this.toast.error({
                message: this.getTranslationForErrorMessage(error?.message, error?.extendedPayload),
              });

              return of(GatewayCommissioningActions.registerGateway.failed());
            })
          )
      )
    );
  });

  commercialRegisterGateway$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.commercialRegisterGateway.attempt),
      mergeMap((action) =>
        combineLatest([
          of(action),
          this.authStore.me$.pipe(take(1)),
          this.gatewayCommissioningStore.formData$.pipe(take(1)),
        ])
      ),
      switchMap(([action, me, formData]) => {
        const email = action.payload.withoutInvitation ? me.email : formData.email;

        return this.equipmentApi
          .createCommercialInstallation({
            accessLevel: InvitationAccessLevel.CommercialOwner,
            address: formData.address,
            description: formData.description,
            email,
            gatewayPassword: formData.gateway.password,
            gatewaySerial: formData.gateway.serial,
            withoutInvitation: action.payload.withoutInvitation,
          })
          .pipe(
            map(() => {
              return GatewayCommissioningActions.commercialRegisterGateway.success();
            }),
            catchError((error) => {
              this.toast.error({
                message: this.getTranslationForErrorMessage(error?.message, error?.extendedPayload),
              });

              return of(GatewayCommissioningActions.commercialRegisterGateway.failed());
            })
          );
      })
    );
  });

  sendInvitation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.sendInvitation.attempt),
      switchMap((action) => {
        return this.invitationsStore.pendingTransferInstallationInvitations$.pipe(
          take(1),
          map((invitations: Invitation[]) => {
            const pendingInvitationOfInviteePresent = invitations.find((invitation) => {
              return invitation.inviteeEmail === action.inviteeEmail;
            });

            if (pendingInvitationOfInviteePresent) {
              return GatewayCommissioningActions.resendInvitation({
                invitationToCancel: {
                  invitationToken: pendingInvitationOfInviteePresent.token,
                },
                invitationToCreate: {
                  installationId: action.installationId,
                  inviteeEmail: action.inviteeEmail,
                },
              });
            } else {
              return GatewayCommissioningActions.createInvitation({
                installationId: action.installationId,
                inviteeEmail: action.inviteeEmail,
              });
            }
          }),
          catchError(() => {
            return of(GatewayCommissioningActions.sendInvitation.failed());
          })
        );
      })
    );
  });

  createInvitation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.createInvitation),
      switchMap((action) => {
        return this.invitationsApi
          .createInvitationToTransferOwnership({
            installationId: action.installationId,
            inviteeEmail: action.inviteeEmail,
          })
          .pipe(
            map(() => {
              this.toast.info({
                message: this.translate.instant(
                  '+GATEWAY_COMMISSIONING.TRANSFER_INSTALLATION.SUCCESS_MESSAGE'
                ),
              });

              return GatewayCommissioningActions.sendInvitation.success();
            }),
            catchError(() => {
              this.toast.error({
                message: this.translate.instant('+GATEWAY_COMMISSIONING.ERROR.INVITATION_ERROR'),
              });

              return of(GatewayCommissioningActions.sendInvitation.failed());
            })
          );
      })
    );
  });

  resendInvitation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.resendInvitation),
      switchMap((action) => {
        return this.invitationsApi
          .updateInvitation(action.invitationToCancel.invitationToken, {
            status: InvitationStatusType.Canceled,
          })
          .pipe(
            map(() => {
              return GatewayCommissioningActions.createInvitation({
                installationId: action.invitationToCreate.installationId,
                inviteeEmail: action.invitationToCreate.inviteeEmail,
              });
            })
          );
      })
    );
  });

  verifyGateway$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GatewayCommissioningActions.verifyGateway.attempt),
      switchMap((action) =>
        this.gatewayApi.verifyGateway(action.gatewaySerial, action.gatewayPassword).pipe(
          map((verifyGateway) =>
            GatewayCommissioningActions.verifyGateway.success({
              verifyGateway: verifyGateway.data,
            })
          ),
          catchError((error: ViError | unknown) =>
            of(GatewayCommissioningActions.verifyGateway.failed({ error }))
          )
        )
      )
    );
  });

  private getTranslationForErrorMessage(
    message: string,
    extendedPayload: {
      [key: string]: string;
    }
  ): string {
    let translation: string;

    switch (message) {
      case 'GATEWAY_NOT_FOUND':
        translation = '+GATEWAY_COMMISSIONING.ERROR.GATEWAY_NOT_FOUND';
        break;
      case 'USER_NOT_FOUND':
        translation = '+GATEWAY_COMMISSIONING.ERROR.USER_NOT_FOUND';
        break;
      case 'PACKAGE_NOT_PAID_FOR':
        translation = '+GATEWAY_COMMISSIONING.ERROR.PACKAGE_NOT_PAID_FOR';
        break;
      case 'GATEWAY_NOT_ONLINE':
        translation = '+GATEWAY_COMMISSIONING.ERROR.GATEWAY_NOT_ONLINE';
        break;
      case 'GATEWAY_HAS_OWNER':
        translation = '+GATEWAY_COMMISSIONING.ERROR.GATEWAY_HAS_OWNER';
        break;
      case 'DEVICE_COMMUNICATION_ERROR':
        translation = '+GATEWAY_COMMISSIONING.ERROR.DEVICE_COMMUNICATION_ERROR';
        break;
      case 'VALIDATION_ERROR':
        if (extendedPayload && extendedPayload.password === 'INVALID') {
          translation = '+GATEWAY_COMMISSIONING.ERROR.WRONG_PASSWORD';
        } else {
          translation = '+GATEWAY_COMMISSIONING.ERROR.GENERAL';
        }
        break;
      case 'EXTERNAL_SERVICE_ERROR':
        translation = '+GATEWAY_COMMISSIONING.ERROR.EXTERNAL_SERVICE_ERROR';
        break;
      case 'UNAUTHORIZED':
        translation = '+GATEWAY_COMMISSIONING.ERROR.UNATHORIZED';
        break;
      default:
        translation = '+GATEWAY_COMMISSIONING.ERROR.GENERAL';
    }

    return this.translate.instant(translation);
  }
}
